import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-final-form';
import {
  setError,
  setResetPopupHandler,
  setLocationsResetButtonIsDisabled
} from '../../../../actions';
import { NewSearchEmpty } from '../../NewSearchCities/NewSearchEmpty';
import { entitiesFilter } from '../../../../utils/helpers/common';
import NewSearchEmptyLocations from '../NewSearchEmptyLocations';
import { NewSearchLocationProps } from './types';
import { selectSearchLocationNames } from '../../../../store/selectors';
import { NewTabContent } from '../NewSearchLocationTabComponent';
import { ListContainer, LocationsListContainer } from './styled';
import { Location } from '../types';
import { Checkbox } from '../../../ui/Checkbox';
import { locationGroupDispatcher } from '../../../../utils/helpers';
import { useGetMaxBlockHeight } from '../useGetMaxBlockHeight';
import { DISTRICT_ID, TOWNSHIP_ID } from '../../../../utils/helpers/constants';
import { getUpdatedChosenLocationIds } from '../../../../utils/helpers/getUpdatedChosenLocationIds';
import { getTownshipsCount } from '../../../../utils/helpers/getTownshipsCount';
import { useCityAndLocationsQuery } from '../../../useCityAndLocationsQuery';

export const NewSearchLocation = ({
  isFocused,
  noLocations,
  searchString,
  cancelHandler,
  openCitiesHandler,
  setChosenLocationIds,
  chosenLocationIds,
  currentTabId,
  locationGroups,
  setTownshipsCount,
  setLocations
}: NewSearchLocationProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getState } = useForm();
  const chosenLocationNames = useSelector(selectSearchLocationNames);
  const cityId = getState().values.city_id || '';
  const { data, loading, error } = useCityAndLocationsQuery(cityId);
  const locations: Location[] = data?.locations || [];
  const filteredLocations = entitiesFilter(locations, searchString);
  const maxBlockHeight = useGetMaxBlockHeight([], filteredLocations);

  useEffect(() => {
    if (!chosenLocationNames.length) {
      setChosenLocationIds([]);
    }
  }, [chosenLocationNames]);

  useEffect(() => {
    setLocations(data?.locations || []);
  }, [data?.locations]);

  const onChangeCheckboxHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentId = e.target.value;
    const updatedLocationIds = () => {
      return chosenLocationIds.includes(currentId)
        ? chosenLocationIds.filter(el => el !== currentId)
        : [...chosenLocationIds, currentId];
    };
    setChosenLocationIds(updatedLocationIds);
  };

  const onCheckGroupHandler = (group: Location[], val: boolean) => {
    const updatedIds = getUpdatedChosenLocationIds(
      group,
      val,
      chosenLocationIds
    );
    setChosenLocationIds(updatedIds);
  };

  useEffect(() => {
    dispatch(setResetPopupHandler(() => setChosenLocationIds([])));

    return () => {
      dispatch(setResetPopupHandler(null));
      dispatch(setLocationsResetButtonIsDisabled(false));
    };
  }, []);

  useEffect(() => {
    dispatch(setLocationsResetButtonIsDisabled(!chosenLocationIds.length));
  }, [chosenLocationIds]);

  useEffect(() => {
    if (error) dispatch(setError(true));
  }, [error]);

  const locationsList = filteredLocations.map(location => (
    <Checkbox
      key={location.id}
      cypress="location"
      name="search-locations"
      currentValues={chosenLocationIds}
      onChangeHandler={onChangeCheckboxHandler}
      label={location.name}
      id={location.id}
      additionalLabel={locationGroupDispatcher(
        location.locationGroupId,
        location.parent,
        t
      )}
      markAlign="left"
    />
  ));

  const townships =
    locationGroups.find(group => group.id === TOWNSHIP_ID)?.locations || [];
  const preparedLocationGroups = locationGroups?.filter(
    gr => gr.id !== TOWNSHIP_ID
  );
  const selectedGroup = preparedLocationGroups[currentTabId];

  useEffect(() => {
    if (townships) {
      setTownshipsCount(getTownshipsCount(townships, chosenLocationIds));
    }
  }, [chosenLocationIds]);

  return (
    <ListContainer>
      {isFocused && searchString && (
        <LocationsListContainer maxBlockHeight={maxBlockHeight}>
          {locationsList}
        </LocationsListContainer>
      )}
      {!searchString && selectedGroup && (
        <NewTabContent
          locations={selectedGroup.locations}
          townships={selectedGroup.id === DISTRICT_ID ? townships : null}
          locationGroupId={selectedGroup.id}
          isFullName={selectedGroup.id === DISTRICT_ID}
          {...{
            chosenLocationIds,
            onCheckGroupHandler,
            onChangeCheckboxHandler,
            setTownshipsCount
          }}
        />
      )}
      {!locationsList.length && searchString && (
        <NewSearchEmpty
          onClickHandler={cancelHandler}
          emptyText={t('search.city_search.empty_search')}
        />
      )}
      {!loading && !locationsList.length && noLocations && (
        <NewSearchEmptyLocations
          onClickHandler={openCitiesHandler}
          linkText={t('new_search.select_other_city')}
        />
      )}
    </ListContainer>
  );
};
